import React from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { formatDateShort, formatTime } from "@/lib/date";
import { TID } from "@/lib/testIds";

/**
 * HorizontalNewsCard — landscape card with image 40–45% and text 55–60%.
 * Title 18–22 px, max 3 lines. Shows category and time.
 *
 * Props:
 *   article
 *   imageRight   bool  default false (image on the left)
 *   equalHeight  bool  default true — stretch to parent height when in a flex/grid row
 *   compact      bool  smaller variant for dense grids
 */
export default function HorizontalNewsCard({
  article,
  imageRight = false,
  equalHeight = true,
  compact = false,
}) {
  if (!article) return null;
  const focal = article.image_focal_point || "50% 40%";
  const dateStr = article.published_at ? formatDateShort(article.published_at) : "";
  const time = article.published_at ? formatTime(article.published_at) : "";

  return (
    <article
      data-testid={TID.newsCard(article.id)}
      className={`grid grid-cols-5 bg-white border border-softblue overflow-hidden group ${equalHeight ? "h-full" : ""}`}
    >
      <Link
        to={`/noticia/${article.slug}`}
        className={`relative overflow-hidden bg-blueblack col-span-2 min-h-[110px] sm:min-h-[140px] ${
          imageRight ? "order-2" : ""
        }`}
      >
        {article.main_image ? (
          <img
            src={article.main_image}
            alt={article.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover card-hover-image"
            style={{ objectPosition: focal }}
          />
        ) : (
          <div className="absolute inset-0 bg-softblue/40" />
        )}
      </Link>
      <div className={`col-span-3 flex flex-col justify-center gap-1.5 ${compact ? "p-3" : "p-4 sm:p-5"} ${imageRight ? "order-1" : ""}`}>
        <Link
          to={`/categoria/${article.main_category_slug}`}
          className="text-[11px] uppercase tracking-[0.14em] font-bold text-turquoise hover:text-nightblue"
        >
          {article.main_category_name}
        </Link>
        <Link to={`/noticia/${article.slug}`} className="block">
          <h3
            className={`font-serif font-bold text-blueblack leading-snug line-clamp-3 group-hover:text-petroleum transition-colors ${
              compact ? "text-base sm:text-lg" : "text-lg sm:text-xl lg:text-[22px]"
            }`}
          >
            {article.title}
          </h3>
        </Link>
        <div className="text-[11px] uppercase tracking-widest text-textgray flex items-center gap-3 mt-auto">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> {dateStr}{time ? ` · ${time} hs` : ""}
          </span>
        </div>
      </div>
    </article>
  );
}
